import { coreApi } from '$api'
import { UnavailabilityRecordType, UnavailabilityStatus } from '$constants'
import {
  IUnavailabilityRecord,
  UnavailabilityRecord,
  UnavailabilityRecordUpdateRequest,
} from '$models'
import { singletonQueryClient } from '$utils'

/**
 * Get all unavailability records filter by the folowing parameters:
 * @param staffMemberId staff member to get the unavailability records for
 * @param from optional from date filter to get unavailability records after this date (eg. "2025-02-20")
 * @param type optional filter to only fetch unavailability records of a certain type
 * @param requestedFrom optional from date filter to get unavailability records requested at or after this date
 * @param requestedTo optional to date filter to get unavailability records requested before or at this date
 * @returns Promise<IUnavailabilityRecord[]>
 */
export const getAll = async (
  staffMemberId?: string,
  from?: string,
  to?: string,
  type?: UnavailabilityRecordType,
  statuses?: UnavailabilityStatus[],
  requestedFrom?: string,
  requestedTo?: string
): Promise<IUnavailabilityRecord[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      'unavailability',
      staffMemberId,
      from,
      to,
      type,
      statuses,
      requestedFrom,
      requestedTo,
    ],
    queryFn: async () => {
      return await coreApi.getUnavailability(
        staffMemberId,
        type,
        from,
        to,
        statuses,
        requestedFrom,
        requestedTo
      )
    },
  })
}

/**
 * Gets an unavailability record by its id.
 * @param id Id of the unavailability record to get
 * @returns the unavailability record
 */
export const getById = async (id: string): Promise<IUnavailabilityRecord> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['unavailability', id],
    queryFn: async () => {
      return await coreApi.getUnavailabilityById(id)
    },
  })
}

/**
 * Create an unavailability record.
 * @param unavailability to create
 * @returns Promise<Unavailability>
 */
export const add = async (
  unavailability: IUnavailabilityRecord
): Promise<IUnavailabilityRecord> => {
  const result = await coreApi.addUnavailability(
    unavailability as UnavailabilityRecord
  )

  return result
}

/**
 * Update an unavailability record status.
 * @param id Id of the unavailability record
 * @param status Status to update
 * @returns Promise<IUnavailabilityRecord>
 */
export const updateStatus = async (
  id: string,
  status: UnavailabilityStatus
): Promise<IUnavailabilityRecord> => {
  const payload = {
    status: status,
  } as UnavailabilityRecordUpdateRequest
  return await coreApi.updateUnavailabilityStatus(id, payload)
}

/**
 * Send an existing unavailability request by id
 * @param id Id of the request to send
 * @returns Promise<IUnavailabilityRecord>
 */
export const sendRequest = async (
  id: string
): Promise<IUnavailabilityRecord> => {
  const result = await coreApi.sendUnavailabilityApprovalRequest(id)
  return result
}
